import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, Heart, Users, Flower2, Download } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputStyle = {
    background: "rgba(255,253,250,0.9)",
    border: "1px solid rgba(212,175,55,0.4)",
    color: "var(--ink)",
};

export default function GuestListPage() {
    const [passcode, setPasscode] = useState("");
    const [usedPasscode, setUsedPasscode] = useState("");
    const [unlocked, setUnlocked] = useState(false);
    const [guestList, setGuestList] = useState([]);
    const [stats, setStats] = useState(null);
    const [passError, setPassError] = useState("");
    const [unlocking, setUnlocking] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const loadGuests = async (code) => {
        setUnlocking(true);
        setPassError("");
        try {
            const res = await axios.post(`${API}/rsvp/guests`, { passcode: code });
            setGuestList(res.data.guests);
            setStats(res.data.stats);
            setUnlocked(true);
            setUsedPasscode(code);
        } catch {
            setPassError("That passcode doesn't match. Please try again.");
        } finally {
            setUnlocking(false);
        }
    };

    const downloadCsv = async () => {
        setDownloading(true);
        try {
            const res = await axios.post(`${API}/rsvp/export`, { passcode: usedPasscode }, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "vijay-laxmi-dileep-guest-list.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Guest list spreadsheet downloaded.");
        } catch {
            toast.error("Could not download the list. Please try again.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="wedding-app min-h-screen" data-testid="guest-list-page">
            <div className="mx-auto max-w-2xl px-6 py-16 sm:py-20">
                <Reveal>
                    <Link
                        to="/"
                        data-testid="back-to-invitation-link"
                        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors"
                        style={{ color: "var(--wine)" }}
                    >
                        <ArrowLeft size={14} /> Back to invitation
                    </Link>

                    <div className="mt-10 text-center">
                        <div
                            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
                            style={{ background: "var(--wine)", border: "2px solid rgba(212,175,55,0.7)" }}
                        >
                            <span className="font-display text-xl" style={{ color: "var(--ivory)" }}>
                                V♡D
                            </span>
                        </div>
                        <p className="eyebrow mt-6">Vijju weds Dileep</p>
                        <h1 className="font-display mt-3 text-3xl tracking-tight sm:text-4xl" style={{ color: "var(--ink)" }}>
                            Family <span className="italic" style={{ color: "var(--wine)" }}>guest list</span>
                        </h1>
                        <p className="mx-auto mt-3 max-w-md text-sm" style={{ color: "var(--muted)" }}>
                            Who's celebrating with us, live. This list is for family; enter the passcode shared with your
                            invitation.
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <div
                        className="gold-ring mt-10 rounded-3xl p-8 sm:p-10"
                        style={{ background: "rgba(255,253,250,0.85)" }}
                        data-testid="guest-list-card"
                    >
                        {!unlocked ? (
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    loadGuests(passcode);
                                }}
                                className="flex flex-col gap-4"
                            >
                                <div className="flex items-center gap-3">
                                    <span
                                        className="flex h-11 w-11 items-center justify-center rounded-full"
                                        style={{ background: "var(--blush)", color: "var(--wine)", border: "1px solid rgba(212,175,55,0.5)" }}
                                    >
                                        <Lock size={17} />
                                    </span>
                                    <p className="text-sm" style={{ color: "var(--muted)" }}>
                                        Enter the family passcode to unlock the live guest list.
                                    </p>
                                </div>
                                <input
                                    data-testid="passcode-input"
                                    type="password"
                                    inputMode="numeric"
                                    value={passcode}
                                    onChange={(e) => setPasscode(e.target.value)}
                                    placeholder="Enter passcode"
                                    maxLength={12}
                                    className="w-full rounded-xl px-4 py-3 text-center text-lg tracking-[0.4em] outline-none focus:ring-2"
                                    style={inputStyle}
                                />
                                {passError && (
                                    <p data-testid="passcode-error" className="text-center text-sm" style={{ color: "#b3403f" }}>
                                        {passError}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    data-testid="passcode-unlock-button"
                                    disabled={unlocking || !passcode.trim()}
                                    className="rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-[0.15em] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                                    style={{ background: "var(--wine)", color: "var(--ivory)" }}
                                >
                                    {unlocking ? "Checking…" : "Unlock guest list"}
                                </button>
                            </form>
                        ) : (
                            <div data-testid="guest-list">
                                <div className="flex flex-wrap items-center justify-center gap-3">
                                    {[
                                        { icon: Users, label: `${stats.responses} responses`, id: "stat-responses" },
                                        { icon: Heart, label: `${stats.attending} attending`, id: "stat-attending" },
                                        { icon: Flower2, label: `${stats.total_guests} guests expected`, id: "stat-guests" },
                                    ].map((s) => (
                                        <span
                                            key={s.id}
                                            data-testid={s.id}
                                            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold"
                                            style={{ background: "var(--blush)", color: "var(--wine)", border: "1px solid rgba(212,175,55,0.4)" }}
                                        >
                                            <s.icon size={13} /> {s.label}
                                        </span>
                                    ))}
                                </div>

                                <div className="mt-5 text-center">
                                    <button
                                        type="button"
                                        data-testid="guest-list-download-button"
                                        onClick={downloadCsv}
                                        disabled={downloading}
                                        className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                                        style={{ border: "1px solid var(--wine)", color: "var(--wine)", background: "transparent" }}
                                    >
                                        <Download size={14} /> {downloading ? "Preparing…" : "Download spreadsheet"}
                                    </button>
                                </div>

                                <div className="mt-6 space-y-3 overflow-y-auto pr-1" style={{ maxHeight: "420px" }}>
                                    {guestList.length === 0 && (
                                        <p data-testid="guest-list-empty" className="py-10 text-center text-sm" style={{ color: "var(--muted)" }}>
                                            No RSVPs yet. Be the first to bless the couple.
                                        </p>
                                    )}
                                    {guestList.map((g) => (
                                        <div
                                            key={g.id}
                                            data-testid={`guest-entry-${g.id}`}
                                            className="rounded-2xl px-5 py-4"
                                            style={{ background: "var(--blush)", border: "1px solid rgba(212,175,55,0.3)" }}
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="font-display text-lg" style={{ color: "var(--ink)" }}>
                                                    {g.name}
                                                </span>
                                                <span
                                                    className="rounded-full px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.12em]"
                                                    style={{
                                                        background: g.attendance === "attending" ? "var(--wine)" : "transparent",
                                                        color: g.attendance === "attending" ? "var(--ivory)" : "var(--wine)",
                                                        border: "1px solid var(--wine)",
                                                    }}
                                                >
                                                    {g.attendance === "attending" ? `Attending${g.guests > 0 ? ` · ${g.guests}` : ""}` : "In spirit"}
                                                </span>
                                            </div>
                                            {g.message && (
                                                <p className="mt-2 text-sm italic" style={{ color: "var(--muted)" }}>
                                                    “{g.message}”
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Reveal>

                <p className="mt-10 text-center text-[0.7rem]" style={{ color: "var(--muted)" }}>
                    24 November 2026 · Bera Hambro Ki Bawri, Rajasthan
                </p>
            </div>
        </div>
    );
}
